/**
 * Naming convention for flag keys:
 *   show*      — controls whether a piece of UI renders
 *   enable*    — controls whether a capability is available
 *   *Mode      — an operational switch, flipped during an incident
 *
 * A flag that has been at 100% for a full release cycle should be deleted from
 * here and from the Firebase console, and its behaviour inlined into the code.
 *
 * Keys and defaults must stay identical across every client repo. Nothing
 * enforces that automatically — a flag change is a PR in each of them.
 */
export enum RemoteConfigKey {
  MaintenanceMode = 'maintenance_enabled'
}

/**
 * Defaults must describe what the app does today, because every failure path
 * resolves to them: Firebase outage, blocked network, or a browser without
 * IndexedDB all return the default rather than the published value.
 */
export type RemoteConfigPrimitive = boolean | number | string;

const DEFAULTS = {
  [RemoteConfigKey.MaintenanceMode]: false
} as const;

/**
 * The intersection stands in for `satisfies`, which needs TypeScript 4.9 and
 * this repo is older: the Record half forces every key to have a default, the
 * `typeof` half keeps each value's literal type so RemoteConfigValueOf can tell
 * a string key from a boolean one.
 */
export const REMOTE_CONFIG_DEFAULTS: Record<RemoteConfigKey, RemoteConfigPrimitive> &
  typeof DEFAULTS = DEFAULTS;

export type RemoteConfigDefaults = typeof REMOTE_CONFIG_DEFAULTS;

export type RemoteConfigValueOf<K extends RemoteConfigKey> = K extends keyof RemoteConfigDefaults
  ? RemoteConfigDefaults[K] extends boolean
    ? boolean
    : RemoteConfigDefaults[K] extends number
    ? number
    : string
  : never;

/**
 * How stale a cached value may be before the SDK refetches. Firebase defaults
 * to 12 hours, which would mean a flag flip takes half a day to reach users who
 * already have values cached.
 */
export const REMOTE_CONFIG_FETCH_INTERVAL_MS =
  process.env.NODE_ENV === 'development' ? 0 : 5 * 60 * 1000;

/**
 * How often mounted subscribers re-check for a new value. Half the fetch
 * interval: polling at exactly that interval is throttled every other time,
 * because the SDK stamps its timestamp once the request completes. The floor
 * stops the unthrottled development value from busy-polling.
 */
export const REMOTE_CONFIG_REFRESH_INTERVAL_MS = Math.max(
  REMOTE_CONFIG_FETCH_INTERVAL_MS / 2,
  10 * 1000
);
